import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import aiDevelopmentService from "../../services/aiDevelopmentService";
import { useAuth } from "../../contexts/AuthContext";
import "./AiDevelopmentStudio.css";

const EMPTY_SPRINT = { name: "", goal: "", startDate: "", endDate: "", capacityPoints: "" };
const NON_GATED_CEREMONIES = ["BACKLOG_REFINEMENT", "DAILY_STANDUP", "SPRINT_REVIEW", "SPRINT_RETROSPECTIVE"];

export default function AiSprintBoardPage() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [sprints, setSprints] = useState([]);
  const [backlog, setBacklog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_SPRINT);

  useEffect(() => {
    aiDevelopmentService.projects().then((list) => setProjects(list || [])).catch(() => setProjects([]));
  }, []);

  const load = useCallback(async () => {
    if (!projectId) {
      setSprints([]);
      setBacklog([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [sprintList, storyPage] = await Promise.all([
        aiDevelopmentService.sprints(projectId),
        aiDevelopmentService.stories("", 0, 200, projectId),
      ]);
      setSprints(sprintList || []);
      const allStories = storyPage?.content || [];
      setBacklog(allStories.filter((s) => !s.sprintId));
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const createSprint = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await aiDevelopmentService.createSprint({
        projectId,
        name: form.name,
        goal: form.goal || null,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        capacityPoints: form.capacityPoints ? Number(form.capacityPoints) : null,
      });
      setForm(EMPTY_SPRINT);
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(err?.message || t("common.errorOccurred"));
    }
  };

  return (
    <div className="ai-studio-page">
      <header className="ai-studio-header">
        <div>
          <h1>{t("aiDevelopment.sprints.title")}</h1>
          <p>{t("aiDevelopment.sprints.subtitle")}</p>
        </div>
      </header>
      {error && <div className="ai-studio-alert">{error}</div>}
      <section className="ai-studio-panel">
        <div className="ai-toolbar">
          <label>
            {t("aiDevelopment.project")}
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">{t("common.select")}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          {projectId && (
            <button className="btn btn-primary" onClick={() => setShowCreate((v) => !v)}>
              {t("aiDevelopment.sprints.newSprint")}
            </button>
          )}
        </div>
        {showCreate && (
          <form className="ai-form-grid" onSubmit={createSprint}>
            <label>
              {t("aiDevelopment.sprints.name")}
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label>
              {t("aiDevelopment.sprints.capacity")}
              <input
                type="number"
                min="0"
                value={form.capacityPoints}
                onChange={(e) => setForm({ ...form, capacityPoints: e.target.value })}
              />
            </label>
            <label className="ai-span-2">
              {t("aiDevelopment.sprints.goal")}
              <textarea value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} />
            </label>
            <label>
              {t("aiDevelopment.sprints.startDate")}
              <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
            </label>
            <label>
              {t("aiDevelopment.sprints.endDate")}
              <input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
            </label>
            <div className="ai-actions ai-span-2">
              <button className="btn btn-primary" type="submit">
                {t("common.create")}
              </button>
              <button type="button" className="btn btn-default" onClick={() => setShowCreate(false)}>
                {t("common.cancel")}
              </button>
            </div>
          </form>
        )}
      </section>
      {loading && !sprints.length ? (
        <p>{t("common.loading")}</p>
      ) : (
        projectId &&
        (sprints.length === 0 ? (
          <p className="ai-repository-empty">{t("aiDevelopment.sprints.empty")}</p>
        ) : (
          sprints.map((sprint) => (
            <SprintCard key={sprint.id} sprint={sprint} backlog={backlog} t={t} onChanged={load} />
          ))
        ))
      )}
    </div>
  );
}

function SprintCard({ sprint, backlog, t, onChanged }) {
  const { hasPermission } = useAuth();
  const canRequestAgentAction = hasPermission("ai.development.request");
  const [expanded, setExpanded] = useState(false);
  const [stories, setStories] = useState([]);
  const [ceremonies, setCeremonies] = useState([]);
  const [error, setError] = useState("");
  const [assignId, setAssignId] = useState("");
  const [ceremonyForm, setCeremonyForm] = useState({ ceremonyType: "DAILY_STANDUP", summary: "" });

  const load = useCallback(async () => {
    setError("");
    try {
      const [storyPage, ceremonyList] = await Promise.all([
        aiDevelopmentService.stories("", 0, 200, sprint.projectId),
        aiDevelopmentService.sprintCeremonies(sprint.id),
      ]);
      setStories((storyPage?.content || []).filter((s) => s.sprintId === sprint.id));
      setCeremonies(ceremonyList || []);
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    }
  }, [sprint.id, sprint.projectId, t]);

  useEffect(() => {
    if (expanded) load();
  }, [expanded, load]);

  const act = async (fn) => {
    setError("");
    try {
      await fn();
      await load();
      onChanged();
    } catch (err) {
      setError(err?.message || t("common.errorOccurred"));
    }
  };

  const pendingPlanning = ceremonies.find((c) => c.ceremonyType === "SPRINT_PLANNING" && c.status === "PENDING_APPROVAL");

  return (
    <section className="ai-studio-panel ai-sprint-card">
      <div className="ai-sprint-card-header" onClick={() => setExpanded((v) => !v)}>
        <strong>{sprint.name}</strong>
        <span className="ai-status">{t(`aiDevelopment.sprints.status.${sprint.status}`)}</span>
        <span>{t("aiDevelopment.sprints.storyCount", { count: sprint.storyCount })}</span>
      </div>
      {expanded && (
        <div>
          {error && <div className="ai-studio-alert">{error}</div>}
          {sprint.goal && <p>{sprint.goal}</p>}
          <h4>{t("aiDevelopment.sprints.assignedStories")}</h4>
          {stories.length === 0 ? (
            <p className="ai-repository-empty">{t("aiDevelopment.sprints.noStories")}</p>
          ) : (
            <ul className="ai-relationship-list">
              {stories.map((s) => (
                <li key={s.id} className="ai-relationship-item">
                  <Link to={`/ai-development/stories/${s.id}`}>{s.title}</Link>
                  <span className="ai-status">{s.status}</span>
                  {sprint.status === "PLANNED" && (
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={() => act(() => aiDevelopmentService.removeStoryFromSprint(s.id))}
                    >
                      {t("common.delete")}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {sprint.status === "PLANNED" && backlog.length > 0 && (
            <div className="ai-relationship-form">
              <select value={assignId} onChange={(e) => setAssignId(e.target.value)}>
                <option value="">{t("common.select")}</option>
                {backlog.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-default"
                disabled={!assignId}
                onClick={() => act(() => aiDevelopmentService.assignStoryToSprint(sprint.id, assignId))}
              >
                {t("aiDevelopment.sprints.assign")}
              </button>
            </div>
          )}

          {sprint.status === "PLANNED" && (
            <div className="ai-actions">
              {!pendingPlanning ? (
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => act(() => aiDevelopmentService.requestSprintPlanning(sprint.id))}
                >
                  {t("aiDevelopment.sprints.requestPlanning")}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() =>
                      act(() =>
                        aiDevelopmentService.decideSprintPlanning(pendingPlanning.id, {
                          version: pendingPlanning.version,
                          approved: true,
                          note: "",
                        }),
                      )
                    }
                  >
                    {t("aiDevelopment.sprints.approvePlanning")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={() =>
                      act(() =>
                        aiDevelopmentService.decideSprintPlanning(pendingPlanning.id, {
                          version: pendingPlanning.version,
                          approved: false,
                          note: "",
                        }),
                      )
                    }
                  >
                    {t("aiDevelopment.sprints.rejectPlanning")}
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => act(() => aiDevelopmentService.startSprint(sprint.id, sprint.version))}
              >
                {t("aiDevelopment.sprints.start")}
              </button>
              <button
                type="button"
                className="btn btn-default"
                onClick={() =>
                  act(() => aiDevelopmentService.cancelSprint(sprint.id, { version: sprint.version, reason: "" }))
                }
              >
                {t("aiDevelopment.sprints.cancel")}
              </button>
            </div>
          )}

          {sprint.status === "ACTIVE" && (
            <>
              <form
                className="ai-relationship-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  act(() =>
                    aiDevelopmentService.recordSprintCeremony(sprint.id, {
                      ceremonyType: ceremonyForm.ceremonyType,
                      summary: ceremonyForm.summary,
                      structuredPayload: null,
                    }),
                  ).then(() => setCeremonyForm({ ...ceremonyForm, summary: "" }));
                }}
              >
                <select
                  value={ceremonyForm.ceremonyType}
                  onChange={(e) => setCeremonyForm({ ...ceremonyForm, ceremonyType: e.target.value })}
                >
                  {NON_GATED_CEREMONIES.map((v) => (
                    <option key={v} value={v}>
                      {t(`aiDevelopment.sprints.ceremonyType.${v}`)}
                    </option>
                  ))}
                </select>
                <input
                  placeholder={t("aiDevelopment.sprints.ceremonySummaryPlaceholder")}
                  value={ceremonyForm.summary}
                  onChange={(e) => setCeremonyForm({ ...ceremonyForm, summary: e.target.value })}
                />
                <button className="btn btn-default" type="submit">
                  {t("aiDevelopment.sprints.recordCeremony")}
                </button>
              </form>
              <div className="ai-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => act(() => aiDevelopmentService.completeSprint(sprint.id, sprint.version))}
                >
                  {t("aiDevelopment.sprints.complete")}
                </button>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() =>
                    act(() => aiDevelopmentService.cancelSprint(sprint.id, { version: sprint.version, reason: "" }))
                  }
                >
                  {t("aiDevelopment.sprints.cancel")}
                </button>
              </div>
            </>
          )}

          <h4>{t("aiDevelopment.sprints.ceremonies")}</h4>
          {ceremonies.length === 0 ? (
            <p className="ai-repository-empty">{t("aiDevelopment.sprints.noCeremonies")}</p>
          ) : (
            <ul className="ai-relationship-list">
              {ceremonies.map((c) => (
                <li key={c.id} className="ai-relationship-item ai-ceremony-item">
                  <div>
                    <span className="ai-status">{t(`aiDevelopment.sprints.ceremonyType.${c.ceremonyType}`)}</span>
                    <span>{c.status}</span>
                    <small>{c.summary}</small>
                  </div>
                  {(c.ceremonyType === "DAILY_STANDUP" || c.ceremonyType === "SPRINT_RETROSPECTIVE") && (
                    <div className="ai-ceremony-agent">
                      {canRequestAgentAction && (
                        <button
                          type="button"
                          className="btn btn-default btn-sm"
                          onClick={() => act(() => aiDevelopmentService.enrichCeremonyWithAgent(c.id))}
                        >
                          {t("aiDevelopment.sprints.scrumMasterEnrich")}
                        </button>
                      )}
                      {c.agentSummary && (
                        <p className="ai-agent-narrative">
                          <em>{t("aiDevelopment.sprints.scrumMasterNarrative")}:</em> {c.agentSummary}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
